import { useEffect, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useLocation } from "react-router-dom";
import {
  registerTurnstileReset,
  setTurnstileToken,
} from "@/lib/turnstile";

const SCRIPT_ID = "cloudflare-turnstile-script";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TURNSTILE_ACTION = "public_application";

let scriptPromise;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const onLoad = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error("Turnstile loaded without its browser API."));
      }
    };
    const onError = () => reject(new Error("Turnstile failed to load."));

    if (existing) {
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener("error", onError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export function ApplicationSecurity() {
  const location = useLocation();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errorCode, setErrorCode] = useState("");

  const isApplyPage = location.pathname === "/apply";

  useEffect(() => {
    if (!isApplyPage) {
      setTurnstileToken("");
      return undefined;
    }

    if (!SITE_KEY) {
      console.error("[FanFuel Turnstile] REACT_APP_TURNSTILE_SITE_KEY is missing.");
      setStatus("configuration-error");
      return undefined;
    }

    let cancelled = false;
    let unregisterReset = () => {};

    setStatus("loading");
    setErrorCode("");
    setTurnstileToken("");

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;

        try {
          widgetIdRef.current = turnstile.render(containerRef.current, {
            sitekey: SITE_KEY,
            action: TURNSTILE_ACTION,
            theme: "dark",
            size: "flexible",
            callback: (value) => {
              setTurnstileToken(value);
              setErrorCode("");
              setStatus("verified");
            },
            "expired-callback": () => {
              setTurnstileToken("");
              setErrorCode("");
              setStatus("expired");
            },
            "timeout-callback": () => {
              setTurnstileToken("");
              setErrorCode("");
              setStatus("expired");
            },
            "error-callback": (code) => {
              const normalizedCode = code ? String(code) : "unknown";
              console.error("[FanFuel Turnstile] widget error:", normalizedCode);
              setTurnstileToken("");
              setErrorCode(normalizedCode);
              setStatus("error");
              return false;
            },
          });

          console.info("[FanFuel Turnstile] widget rendered:", widgetIdRef.current);

          unregisterReset = registerTurnstileReset(() => {
            setTurnstileToken("");
            setErrorCode("");
            setStatus("loading");
            if (widgetIdRef.current !== null) turnstile.reset(widgetIdRef.current);
          });
        } catch (error) {
          console.error("[FanFuel Turnstile] render failed:", error);
          setTurnstileToken("");
          setErrorCode("render-failed");
          setStatus("error");
        }
      })
      .catch((error) => {
        console.error("[FanFuel Turnstile] script initialization failed:", error);
        if (!cancelled) {
          setErrorCode("script-load-failed");
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
      unregisterReset();
      setTurnstileToken("");
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [isApplyPage]);

  if (!isApplyPage) return null;

  const statusText = {
    loading: "Checking your browser…",
    verified: "Security check complete.",
    expired: "The check expired and is refreshing…",
    error: `Security check unavailable. Reference: ${errorCode || "unknown"}.`,
    "configuration-error": "Security check is not configured.",
  }[status];

  return (
    <aside
      aria-label="Application security check"
      className="fixed bottom-4 left-4 right-4 z-[80] rounded-2xl border border-white/10 bg-ff-slate/95 p-4 shadow-2xl backdrop-blur md:left-auto md:w-[360px]"
      data-testid="application-security"
      data-turnstile-error={errorCode || undefined}
    >
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ff-ivory">
        <ShieldCheck className="h-4 w-4 text-ff-teal" />
        Security check
      </div>
      <div ref={containerRef} className="min-h-[65px] w-full" />
      <p className="mt-2 text-xs text-ff-grey" aria-live="polite">
        {statusText}
      </p>
    </aside>
  );
}

export default ApplicationSecurity;
